import { Button, Form, message, Input, Col, Row, Modal } from 'antd';
import AppHeader from 'components/AppHeader';
import React, { useState, useEffect } from 'react';
import monthlyCreditApis from 'api/monthlyCreditApis';
import { ContentRow } from 'components/ThemeComponents';
import { StyledTable, OFFSET_HEADER } from 'components/ThemeComponents';
import { fullDateTimeFormat, onlyDateFormat } from 'helpers/dateTimeHelper';
import { allow_manager_roles } from 'helpers/authorizeHelper';

export default function ListCreditsPage() {
  const [monthlyCredits, setMonthlyCredits] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [paginate, setPaginate] = useState({})
  const [searchForm] = Form.useForm();
  
  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalForm] = Form.useForm();
  const [isUpdating, setIsUpdating] = useState(false);

  async function fetchMonthlyCredits(params={}) {
    setIsFetching(true);
    try {
      const res = await monthlyCreditApis.listing(params);
      if (res.code === 200) {
        setMonthlyCredits(res.data ?? []);
        setPaginate({page: res.page, limit: res.limit, total: res.total})
      }
    } catch (error) {
      message.info(error.message);
    }
    setIsFetching(false);
  }

  useEffect(() => {
    fetchMonthlyCredits()
  }, []);

  const onChangeTable = async(pagination, filters, sorter) => {
    var params = {
      page: pagination.current,
      limit: pagination.pageSize,
      data_sort: sorter.columnKey,
      order_sort: sorter.order
    }
    const args = Object.assign({}, params, searchForm.getFieldsValue())
    await fetchMonthlyCredits(args)
  }

  const onFinishSearchForm = async (values) => {
    const params = values || {}
    await fetchMonthlyCredits(params)
  };

  // Modal functions
  const showUpdateQuotaModal = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
    modalForm.setFieldsValue({
      quota_limit: record.quota_limit,
      quota_used: record.quota_used
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
    modalForm.resetFields();
  };

  const handleUpdateQuota = async (values) => {
    if (!selectedRecord) return;
    
    setIsUpdating(true);
    try {
      const res = await monthlyCreditApis.updateQuota(selectedRecord.id, {
        quota_limit: Number(values.quota_limit),
        quota_used: Number(values.quota_used)
      });
      
      if (res.code === 200) {
        message.success('Cập nhật quota thành công!');
        
        // Cập nhật record trong state thay vì fetch lại toàn bộ
        setMonthlyCredits(prevCredits => 
          prevCredits.map(credit => 
            credit.id === selectedRecord.id 
              ? { ...credit, ...res.data } // Merge với data trả về từ API
              : credit
          )
        );
        
        setIsModalVisible(false);
        setSelectedRecord(null);
        modalForm.resetFields();
      } else {
        message.error(res.message || 'Có lỗi xảy ra khi cập nhật quota');
      }
    } catch (error) {
      message.error(error.message || 'Có lỗi xảy ra khi cập nhật quota');
    }
    setIsUpdating(false);
  };

  const bottom = 'bottomRight';

  const columns = [
    {
      title: 'ID',
      width: '15rem',
      render: (record) => <span>{record.id}</span>
    },
    {
      title: 'tenant_id',
      width: '15rem',
      render: (record) => <span>{record.tenant_id}</span>
    },
    {
      title: 'order_payment_id',
      width: '10rem',
      render: (record) => <span>{record.order_payment_id}</span>
    },
    {
      title: 'subscription_id',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{record.subscription_id}</span>
    },
    {
      title: 'status',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => (
        <span style={{ color: record.status === 'active' ? '#52c41a' : '#ff4d4f' }}>
          {record.status}
        </span>
      )
    },
    {
      title: 'quota_limit',
      width: '10rem',
      render: (record) => <span>{record.quota_limit}</span>
    },
    {
      title: 'quota_used',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{record.quota_used}</span>
    },
    {
      title: 'start_date',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{onlyDateFormat(record.start_date)}</span>
    },
    {
      title: 'end_date',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{onlyDateFormat(record.end_date)}</span>
    },
    {
      title: 'created_at',
      width: '10rem',
      key: 'created_at',
      sorter: true,
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{fullDateTimeFormat(record.created_at)}</span>
    },
    {
      title: 'updated_at',
      width: '10rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => <span>{fullDateTimeFormat(record.updated_at)}</span>
    },
    ( allow_manager_roles(['super_admin'])  ?
      {
        title: 'Actions',
        width: '15rem',
        ellipsis: {
          showTitle: false
        },
        render: (record) => (
          <Button 
            type="primary" 
            size="small"
            onClick={() => showUpdateQuotaModal(record)}
          >
            Cập nhật quota
          </Button>
        )
      } : {}
    )
  ];
  
  return (
    <>
      <AppHeader
        fixedTop
        pageTitle="Tất cả monthly credits"
        pageHeaderExtras={
          <>
          </>
        }
      />
      <br />
      <ContentRow capped>
        <Form
          form={searchForm}
          name="advanced_search"
          className="ant-advanced-search-form"
          onFinish={onFinishSearchForm}
          style={{marginBottom: "40px"}}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name='tenant_id'
                label='tenant_id'
              >
                <Input placeholder="Tìm theo tenant_id" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='domain'
                label='domain'
              >
                <Input placeholder="Tìm theo domain" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='order_payment_id'
                label='order_payment_id'
              >
                <Input placeholder="Tìm theo order_payment_id" />
              </Form.Item>
            </Col>
            <Col
              style={{
                textAlign: 'right',
              }}
            >
              <Button type="primary" htmlType="submit">
                Search
              </Button>
              <Button
                style={{
                  margin: '0 8px',
                }}
                onClick={() => {
                  searchForm.resetFields();
                  onFinishSearchForm();
                }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Form>
        <StyledTable
          sticky={{ offsetHeader: OFFSET_HEADER }}
          size="small"
          scroll={{ x: 1000 }}
          loading={isFetching}
          rowKey="id"
          columns={columns}
          dataSource={monthlyCredits}
          onChange={onChangeTable}
          pagination={{
            defaultPageSize: paginate.limit,
            defaultCurrent: paginate.page,
            total: paginate.total,
            position: [bottom],
            showTotal: (total, range) => (
              <span style={{ left: 0, position: 'absolute' }}>
                Hiển thị {range[0]}-{range[1]} của {total} tin đã đăng
              </span>
            )
          }}
        />
      </ContentRow>

      {/* Modal cập nhật quota */}
      <Modal
        title="Cập nhật Quota"
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
      >
        <Form
          form={modalForm}
          onFinish={handleUpdateQuota}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="quota_limit"
            label="Quota Limit"
            rules={[
              { required: true, message: 'Vui lòng nhập quota limit!' },
              { 
                validator: (_, value) => {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) {
                    return Promise.reject(new Error('Quota limit phải là số và lớn hơn hoặc bằng 0!'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" placeholder="Nhập quota limit" />
          </Form.Item>

          <Form.Item
            name="quota_used"
            label="Quota Used"
            rules={[
              { required: true, message: 'Vui lòng nhập quota used!' },
              { 
                validator: (_, value) => {
                  const num = Number(value);
                  if (isNaN(num) || num < 0) {
                    return Promise.reject(new Error('Quota used phải là số và lớn hơn hoặc bằng 0!'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" placeholder="Nhập quota used" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleModalCancel} style={{ marginRight: 8 }}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={isUpdating}>
              Cập nhật
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
